import * as Popover from '@radix-ui/react-popover';
import type { AgentTemplate } from '@reach/shared-types/agent-templates';
import { ChevronDown, Settings2, TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';
import type { GrantableTool } from './agent-templates-api';
import { CARD_COPY, TEMPLATE_COPY } from './agent-templates-copy';
import type { AgentTemplateGrantTarget } from './grant-key';
import { describeGrants, sortGrantedTools } from './template-grants';
import styles from './TemplateCard.module.css';
import { TemplateKindIcon } from './TemplateKindIcon';
import { TemplateToolsEditor } from './TemplateToolsEditor';

interface TemplateCardProps {
  template: AgentTemplate;
  tools: GrantableTool[] | undefined;
  pendingGrantKey: string | null;
  onAddGrant: (target: AgentTemplateGrantTarget) => void;
  onRemoveGrant: (grantId: string) => void;
}

export const TemplateCard = ({
  template,
  tools,
  pendingGrantKey,
  onAddGrant,
  onRemoveGrant,
}: TemplateCardProps) => {
  const headingId = useId();
  const [editing, setEditing] = useState(false);
  const { title, description } = TEMPLATE_COPY[template.kind];
  const grantedTools = sortGrantedTools(describeGrants(template, tools));
  const hasGrantedTools = grantedTools.length > 0;
  const toolsAreLoaded = tools !== undefined;

  return (
    <section className={styles.card} aria-labelledby={headingId}>
      <header className={styles.header}>
        <h3 id={headingId}>
          <TemplateKindIcon kind={template.kind} />
          {title}
        </h3>
        <Popover.Root open={editing && toolsAreLoaded} onOpenChange={setEditing}>
          <Popover.Trigger asChild>
            <button type="button" className={styles.manageTools} disabled={!toolsAreLoaded}>
              <Settings2 />
              {CARD_COPY.manageTools}
              <ChevronDown />
            </button>
          </Popover.Trigger>
          {editing && tools !== undefined && (
            <TemplateToolsEditor
              template={template}
              tools={tools}
              unavailableGrants={grantedTools.filter(granted => granted.available === false)}
              pendingGrantKey={pendingGrantKey}
              onAddGrant={onAddGrant}
              onRemoveGrant={onRemoveGrant}
            />
          )}
        </Popover.Root>
      </header>
      <p className={styles.description}>{description}</p>
      {hasGrantedTools ? (
        <ul className={styles.tools}>
          {grantedTools.map(granted => (
            <li
              key={granted.grant.grantId}
              data-available={granted.available ?? undefined}
              title={granted.available === false ? CARD_COPY.unavailableTool : undefined}>
              {granted.available === false && <TriangleAlert aria-hidden="true" />}
              {granted.label}
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.noTools}>{CARD_COPY.noTools}</p>
      )}
    </section>
  );
};
